import { useCallback, useEffect, useRef, useState } from 'react';
import { fetchSubscriptionMarkets } from '../services/payment.service';
import type { SubscriptionMarket } from '../types/payment.types';
import { resolveSubscriptionMarket, storeSubscriptionMarket } from '../utils/subscription-market';

type SubscriptionMarketStatus = 'loading' | 'ready' | 'error';

export const useSubscriptionMarket = (requestedMarketCode?: string | null) => {
    const [markets, setMarkets] = useState<SubscriptionMarket[]>([]);
    const [selectedMarket, setSelectedMarket] = useState<SubscriptionMarket | null>(null);
    const [status, setStatus] = useState<SubscriptionMarketStatus>('loading');
    const [reloadGeneration, setReloadGeneration] = useState(0);
    const requestedMarketCodeRef = useRef(requestedMarketCode);
    requestedMarketCodeRef.current = requestedMarketCode;

    useEffect(() => {
        let active = true;
        setStatus('loading');
        fetchSubscriptionMarkets()
            .then((catalog) => {
                if (!active) return;
                const resolvedMarket = resolveSubscriptionMarket(catalog, requestedMarketCodeRef.current);
                if (!resolvedMarket) throw new Error('No subscription markets are available.');
                storeSubscriptionMarket(resolvedMarket.code);
                setMarkets(catalog.markets);
                setSelectedMarket(resolvedMarket);
                setStatus('ready');
            })
            .catch(() => {
                if (!active) return;
                setMarkets([]);
                setSelectedMarket(null);
                setStatus('error');
            });
        return () => {
            active = false;
        };
    }, [reloadGeneration]);

    useEffect(() => {
        if (status !== 'ready' || !requestedMarketCode) return;
        const requestedMarket = markets.find((market) => (
            market.code.toLowerCase() === requestedMarketCode.trim().toLowerCase()
        ));
        if (requestedMarket) {
            storeSubscriptionMarket(requestedMarket.code);
            setSelectedMarket(requestedMarket);
        }
    }, [markets, requestedMarketCode, status]);

    const selectMarket = useCallback((marketCode: string) => {
        const market = markets.find((candidate) => candidate.code.toLowerCase() === marketCode.toLowerCase());
        if (!market) return;
        storeSubscriptionMarket(market.code);
        setSelectedMarket(market);
    }, [markets]);

    const reloadMarkets = useCallback(() => {
        setReloadGeneration((generation) => generation + 1);
    }, []);

    return { markets, selectedMarket, selectMarket, reloadMarkets, status };
};
